import type { SxProps } from '@mui/material/styles';
import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { Box, ButtonBase, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { BlockingTexts, ModalContextType } from './context';
import { useAppDispatch } from '~/hooks/store';
import { confirmation } from '~/store/ux';

import { ModalContext } from './context';
import sx from './sx';

interface Props {
  open: boolean;
  children?: React.ReactNode;
  modalSx?: SxProps;
  onClose: () => void;
}

const EMPTY_OBJ = {};

const Modal = (props: Props) => {
  const { children, open, modalSx = EMPTY_OBJ, onClose } = props;
  const [blockingTexts, setBlockingTexts] = React.useState<BlockingTexts>({
    message: 'Are you sure you want to close this popup?',
  });
  const isBlockedRef = React.useRef(false);
  const theme = useTheme();
  const style = sx(theme);
  const dispatch = useAppDispatch();

  const context = React.useMemo<ModalContextType>(
    () => ({
      setBlockingTexts,
      setIsBlocked(value: boolean) {
        isBlockedRef.current = value;
      },
      close() {
        if (isBlockedRef.current) {
          void dispatch(
            confirmation({
              title: blockingTexts.message || 'Close modal?',
              confirmBtn: blockingTexts.confirmLabel ?? 'Continue',
              cancelBtn: blockingTexts.cancelLabel ?? 'Cancel',
              callback: result => {
                if (result) {
                  onClose();
                }
              },
            })
          );
        } else {
          onClose();
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [blockingTexts, onClose]
  );

  if (!open) {
    return null;
  }

  const onCloseClick = () => {
    context.close();
  };

  return (
    // eslint-disable-next-line react-x/no-context-provider
    <ModalContext.Provider value={context}>
      <Box sx={style.container}>
        <Paper sx={[style.modal, modalSx] as SxProps}>
          <ButtonBase sx={style.closeBtn} onClick={onCloseClick}>
            <CloseIcon />
          </ButtonBase>
          <Box sx={style.modalContent}>{children}</Box>
        </Paper>
      </Box>
    </ModalContext.Provider>
  );
};

export { ModalContext, ModalContextType };

export default Modal;
